import type { DocPageContent } from '../docs-content'

const page = {
  order: 13,
  slug: 'effects-color',
  title: 'Efectos y Color',
  description:
    'Añade y apila efectos acelerados por GPU, gradúa con ruedas y curvas, lee los scopes, aplica LUTs y aísla correcciones.',
  category: 'Herramientas Creativas',
  related: ['keyframes', 'shapes-masks', 'properties'],
  sections: [
    {
      title: 'Añadir y gestionar efectos',
      blocks: [
        {
          kind: 'steps',
          items: [
            'Selecciona un clip y abre la sección **Efectos** en Propiedades.',
            'Usa **Añadir efecto**, luego busca o elige de una categoría. También puedes añadir **Preajustes** integrados o tus propios preajustes guardados.',
            'Reordena la pila con **Subir** y **Bajar** — el orden cambia cómo se combinan los efectos.',
            'Desactiva, restablece o elimina un efecto desde su panel; los parámetros numéricos se pueden animar con fotogramas clave.',
          ],
        },
      ],
    },
    {
      title: 'El catálogo de efectos',
      blocks: [
        {
          kind: 'table',
          headers: ['Categoría', 'Efectos'],
          rows: [
            [
              'Color',
              'Brillo, Contraste, Exposición, Saturación, Vibrance, Desplazamiento de Tono, Temperatura, Niveles, Curvas, Ruedas de Color, Mapa de Gradiente, LUT',
            ],
            ['Desenfoque', 'Gaussiano, Caja, Movimiento, Radial, Zoom'],
            [
              'Distorsión',
              'Pixelado, División RGB, Torbellino, Onda, Abultar/ Pellizcar, Caleidoscopio, Espejo, Vidrio Esmerilado, Vidrio Ondulado, Mosaico de Vidrio, Bloques, Droste y más',
            ],
            [
              'Estilización',
              'Viñeta, Grano de Película, Enfocar, Brillo, Líneas de Escaneo, CRT, VHS, Semitonos, Dither, ASCII, Tinta, Ordenación de Píxeles, Glitch de Bloques, Glitch de Color',
            ],
            ['Clave', 'Clave de Croma (eliminación de fondo verde y azul)'],
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Todos los efectos se renderizan en la GPU (WebGPU). Las adiciones recientes incluyen **Mapa de Gradiente**, **VHS**, **CRT**, **Droste**, **Glitch de Bloques**, las distorsiones **Vidrio Ondulado** y **Mosaico de Vidrio**, **Tinta** y **Ordenación de Píxeles**.',
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '**ASCII** puede renderizar sus propios caracteres: establece **Conjunto de Caracteres** en **Personalizado**, escribe tus **Caracteres Personalizados** (la rampa va de más denso a más claro) y elige una **Fuente** (Monoespaciada, Courier, Consolas o Lucida Console). Los conjuntos integrados incluyen Rampa ASCII, Denso, Binario y Símbolos.',
        },
      ],
    },
    {
      title: 'Entradas de color y el cuentagotas',
      blocks: [
        {
          kind: 'list',
          items: [
            'Cada control de color acepta un valor **hexadecimal**; escríbelo directamente en el campo (se actualiza la vista previa mientras escribes).',
            'Cuando se admite transparencia, el campo acepta hexadecimal de 8 dígitos (`#rrggbbaa`) y el selector añade un deslizador alfa.',
            'Haz clic en el **cuentagotas** (pipeta) para muestrear un color de cualquier parte de la aplicación o vista previa; una lupa de aumento sigue al cursor para que puedas seleccionar el píxel exacto, luego haz clic para aplicar.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'El cuentagotas es un muestreador integrado en la aplicación y no necesita permiso. La misma herramienta respalda las ruedas en el espacio de trabajo Color.',
        },
      ],
    },
    {
      title: 'Graduar en el espacio de trabajo Color',
      blocks: [
        {
          kind: 'list',
          items: [
            'Cambia al espacio de trabajo **Color** (`Alt+2`) y selecciona un clip; las ruedas y curvas están listas incluso antes de que exista una graduación.',
            '**Ruedas de Color** ajustan Sombras, Tonos Medios, Altas Luces y Compensación, con selectores de balance de blancos y balance automático.',
            '**Curvas** editan los canales Maestro, Rojo, Verde y Azul; haz clic en una curva para añadir un punto y haz doble clic en un punto para eliminarlo.',
            'Compara con **Antes**, **Después** y **Dividido**, y copia un look entre clips con **Copiar Graduación** y **Pegar Graduación**.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '**Omitir graduación** previsualiza la imagen sin graduar pero nunca afecta a las exportaciones.',
        },
      ],
    },
    {
      title: 'Leer los scopes',
      blocks: [
        {
          kind: 'table',
          headers: ['Scope', 'Muestra'],
          rows: [
            ['Forma de Onda', 'Distribución de luminancia y color, de arriba a abajo'],
            ['Desfile RGB', 'Los canales rojo, verde y azul lado a lado'],
            ['Vectorscopio', 'Tono y saturación'],
            ['Histograma', 'La distribución de tonos'],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Activa los scopes con **Mostrar Scopes de Color**. La Forma de Onda y el Histograma también ofrecen vistas RGB, de canal único y de luma (Y).',
        },
      ],
    },
    {
      title: 'LUTs y correcciones aisladas',
      blocks: [
        {
          kind: 'list',
          items: [
            'Añade el efecto **LUT (.cube)**, elige **Importar LUT .cube** y luego establece **Intensidad** para mezclarlo.',
            'El **Calificador Secundario** clavea una corrección a un rango de tono, saturación y luminancia.',
            'La **Ventana de Potencia** limita una corrección a una máscara elíptica o rectangular con suavizado e invertir.',
            'Añade una **capa de ajuste** para graduar o aplicar efectos a cada clip en las pistas que tiene debajo.',
          ],
        },
        {
          kind: 'note',
          tone: 'warning',
          text: 'La importación de LUT requiere un CachoCut actualizado.',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page